import dayjs, { Dayjs } from "dayjs";
import { TimeZone } from "../../features/set-up-spaces-flow/models";
import {
    DATE_FORMAT_ISO,
    DATE_TIME_FORMAT,
    DATE_TIME_FORMAT_WITH_SECONDS,
} from "../constants";

const timeZoneRegExpr = /^\((UTC(?:[-+]\d{2}[:]\d{2})?)\).+$/;
export const mdyyyyRegExp =
    /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/;

export const SECONDS_IN_MINUTE = 60;
export const SECONDS_IN_HOUR = 3600;
export const SECONDS_IN_DAY = 86400;

export const getDateFromTime = (time?: string | null) => {
    if (!time) {
        return;
    }
    const [hours, minutes, seconds] = time.split(":");

    const currentDate = dayjs(); // Get the current date
    const modifiedDate = currentDate
        .set("hour", Number(hours))
        .set("minute", Number(minutes))
        .set("second", Number(seconds || 0))
        .set("millisecond", 0);

    return modifiedDate.toDate();
};

export const getTimeFromDate = (date?: Date | string, timeZone?: string) =>
    dayjs.utc(date).tz(timeZone).format("HH:mm");

export const getTimeFormatForRequest = (time: string) =>
    dayjs(getDateFromTime(time)).format("HH:mm:ss");

export const isTimeBefore = (time1?: string, time2?: string): boolean => {
    const date1 = getDateFromTime(time1);
    const date2 = getDateFromTime(time2);
    if (!date1 || !date2) {
        return false;
    }
    return date1.getTime() < date2.getTime();
};

export const formatDateTimeUtc = (date: Date) =>
    dayjs(date).utcOffset(0, true).format(DATE_FORMAT_ISO);

export const formatTime = (time?: string | null) =>
    getTimeFromDate(getDateFromTime(time));

export const formatHourOfDay = (hour: number) =>
    `${String(hour).padStart(2, "0")}:00`;

export const formatDayOfMonth = (
    date?: string | number | Dayjs | Date | null
) => dayjs(date).format("DD.MM");

export const formatDate = (
    date?: string | number | Dayjs | Date | null,
    timeZone?: string,
    format?: string
) => {
    const parsedDate = timeZone ? dayjs.utc(date).tz(timeZone) : dayjs(date);
    return parsedDate.format(format ?? "DD.MM.YYYY");
};

export const formatDateTime = (
    date?: string | number | Dayjs | Date | null,
    timeZone?: string
) => dayjs.utc(date).tz(timeZone).format(DATE_TIME_FORMAT);

export const formatDateTimeWithSeconds = (
    date?: string | number | Dayjs | Date | null,
    timeZone?: string
) => dayjs.utc(date).tz(timeZone).format(DATE_TIME_FORMAT_WITH_SECONDS);

export const dateTimeWithSeconds = (
    date?: string | number | Dayjs | Date | null,
    timeZone?: string
) => dayjs.utc(date).tz(timeZone);

export const setTimeForDate = (
    date: Date | string,
    time: string,
    timeZone?: string
) => {
    const parsedDate = dayjs(date);
    const [hours, minutes, seconds] = time.split(":");

    const updatedDate = dayjs
        .utc()
        .set("year", parsedDate.year())
        .set("month", parsedDate.month())
        .set("date", parsedDate.date())
        .set("hour", Number(hours))
        .set("minute", Number(minutes))
        .set("second", Number(seconds || 0));

    return dayjs.tz(updatedDate.format(), timeZone).format();
};

export const getUtcDate = (date?: Date | string) => dayjs.utc(date).toDate();

export const getUtcOffset = (timeZone?: TimeZone) =>
    timeZoneRegExpr.exec(timeZone?.displayName ?? "")?.[1];

export const getStartOfDay = (date: Date, timeZone?: string) => {
    const utcDate = dayjs(date).utcOffset(0, true).startOf("day");
    return dayjs.tz(utcDate.format(), timeZone).format();
};

export const getEndOfDay = (date: Date, timeZone?: string) => {
    const utcDate = dayjs(date).utcOffset(0, true).endOf("day");
    return dayjs.tz(utcDate.format(), timeZone).format();
};

export const getTimeZoneFormat = (date: Date | string, timeZone?: string) =>
    dayjs.tz(date, timeZone).format();

export const convertTimeZoneDate = (date: string, timeZone?: string) =>
    dayjs(date).tz(timeZone).toDate();

export const convertUtcToLocalDate = (date: string, timeZone?: string) =>
    dayjs(formatDateTime(date, timeZone), DATE_TIME_FORMAT).toDate();

// prevent: dayjs returns true for dayjs(undefined).isValid()
export const isDateValid = (date?: string | undefined): boolean =>
    !!date && dayjs(date).isValid();

export const trimSecondsFromTime = (time?: string) => {
    const parsed = dayjs(time, ["HH:mm:ss", "HH:mm"], true); // strict parsing with 'true'
    return parsed.isValid() ? parsed.format("HH:mm") : "";
};
